import AppContext from 'App/AppContext';
import { IPCheckbox, IPSearchBox, IPSpinner, IPText } from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import ScrollableStackItem from 'components/Utils/ScrollableStackItem';
import { globalAdminTenantCommentName, globalFilterDelay, globalMinFilterChars } from 'globalConstants';
import { globalStackTokensGapMedium } from 'globalStyles';
import { TFunction } from 'i18next';
import Activity from 'models/activity';
import ActivityTrailModel from 'models/ActivityTrail';
import AuditTrailEntry from 'models/auditTrailEntry';
import { AuthSchemaLineOperation } from 'models/auth/authSchemaLine';
import { CommentTrailEntry } from 'models/comments';
import Entity, { EntityTypes } from 'models/entity';
import User from 'models/user';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetActivityTrail, apiGetActivityTrailForTenant } from 'services/Api/activityService';
import {
  apiAddComment,
  apiAddCommentForSpecificTenant,
  apiRemoveComment,
  apiUpdateComment,
} from 'services/Api/commentService';
import { apiRequest } from 'services/Auth/authConfig';
import { hasUserDataPermission } from 'services/Auth/featurePermissions';
import { newGuidNil } from 'utils/guid';
import { LocalStorageKeys, getLocalStorageData, setLocalStorageData } from 'utils/localstorage';
import { ActivityTrailItem } from './ActivityTrailItem';
import AddComment from './AddComment';
import PendingComment from './PendingComment';
import { IPList } from 'baseComponents/List/IPList';

interface IActivityTrailProps {
  commentTrailId: number;
  auditTrailId: number;
  taskId?: number;
  controlId?: number;
  riskId?: number;
  themeId?: number;
  objectiveId?: number;
  processId?: number;
  packageId?: number;
  assetId?: number;
  processingActivityId?: number;
  internalAuditId?: number;
  // one audit criterion: a comment added here lands on the criterion's own trail
  auditCriterionId?: number;
  authSchemaIds: (number | undefined)[];
  onAddComment?: (comment: CommentTrailEntry) => void;
  onError?: (error: string | unknown) => void;
  tenantId?: string;
  // Stops new comments on top of what the permissions already say, for a caller that knows the record
  // is frozen (an audit execution task whose audit is completed). Left out, the permissions decide on
  // their own, as before.
  readOnly?: boolean;
  // For an entity that is not saved yet (no comment trail exists): messages are queued by the caller
  // instead of posted, and nothing is loaded. Ignored once commentTrailId is set.
  pendingComments?: string[];
  onQueueComment?: (comment: string) => void;
  onRemovePendingComment?: (index: number) => void;
  // Reports the number of messages in the loaded trail, after every load, add, edit and remove.
  onCommentCountChange?: (count: number) => void;
}

const ActivityTrail = (props: IActivityTrailProps) => {
  const appContext = useContext(AppContext);
  // 'audit' carries the audit status names used by the InternalAudit activity texts
  const { t } = useTranslation([
    'translation',
    'commentTrail',
    'auditTrail',
    'audit',
    'adminAuth',
    'library',
    'control',
    'package',
    'risk',
    'objective',
    'asset',
    'processingActivity',
    // the state and field names of an audit criterion's change entries
    'criteria',
  ]);
  const [searchTimer, setSearchTimer] = useState<NodeJS.Timeout | undefined>(undefined);
  const [activities, setActivities] = useState<(CommentTrailEntry | Activity | AuditTrailEntry)[]>([]);
  const [activitiesDisplay, setActivitiesDisplay] = useState<(CommentTrailEntry | Activity | AuditTrailEntry)[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isActionPending, setIsActionPending] = useState<boolean>(false);
  const [entity, setEntity] = useState<Entity | undefined>(undefined);
  const [searchText, setSearchText] = useState<string>('');
  const [onlyShowComments, setOnlyShowComments] = useState<boolean>(
    getLocalStorageData(appContext, LocalStorageKeys.OnlyShowCommentsInActivityTab) === 'true',
  );
  const hasLoaded = useRef<boolean>(false);
  const isQueueMode = !props.commentTrailId && props.onQueueComment !== undefined;
  //derived, not state: props.readOnly can arrive after the first render (the audit status behind an
  //execution task is loaded asynchronously), and a state initializer would never see it
  const canUpdate =
    props.readOnly !== true && hasUserDataPermission(appContext, props.authSchemaIds, AuthSchemaLineOperation.Update);

  useEffect(() => {
    //clean-up
    return () => {
      if (searchTimer) {
        clearTimeout(searchTimer);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    //re-create list to render current pending status
    setActivitiesDisplay([...activitiesDisplay]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActionPending]);

  useEffect(() => {
    if (isQueueMode) {
      return;
    }

    if (
      props.commentTrailId ||
      props.taskId ||
      props.controlId ||
      props.riskId ||
      props.themeId ||
      props.objectiveId ||
      props.processId ||
      props.packageId ||
      props.assetId ||
      props.auditTrailId ||
      props.processingActivityId ||
      props.internalAuditId ||
      props.auditCriterionId
    ) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.commentTrailId,
    props.taskId,
    props.controlId,
    props.riskId,
    props.themeId,
    props.objectiveId,
    props.processId,
    props.packageId,
    props.auditTrailId,
    props.assetId,
    props.processingActivityId,
    props.internalAuditId,
    props.auditCriterionId,
  ]);

  useEffect(() => {
    if (!hasLoaded.current) return;
    props.onCommentCountChange?.(activities.filter((activity) => activity instanceof CommentTrailEntry).length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activities]);

  useEffect(() => {
    onFilter(undefined, searchText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyShowComments]);

  const loadData = async () => {
    try {
      if (isLoading) {
        return;
      }

      setIsLoading(true);

      //load Data
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);

      let entityType = -1;
      let entityId = -1;

      if (props.taskId !== undefined) {
        entityType = EntityTypes.Task;
        entityId = props.taskId;
      }

      if (props.controlId !== undefined) {
        entityType = EntityTypes.Control;
        entityId = props.controlId;
      }

      if (props.riskId !== undefined) {
        entityType = EntityTypes.Risk;
        entityId = props.riskId;
      }

      if (props.themeId !== undefined) {
        entityType = EntityTypes.Requirement;
        entityId = props.themeId;
      }
      if (props.objectiveId !== undefined) {
        entityType = EntityTypes.Objective;
        entityId = props.objectiveId;
      }

      if (props.processId !== undefined) {
        entityType = EntityTypes.Process;
        entityId = props.processId;
      }

      if (props.packageId !== undefined) {
        entityType = EntityTypes.Package;
        entityId = props.packageId;
      }

      if (props.assetId !== undefined) {
        entityType = EntityTypes.Asset;
        entityId = props.assetId;
      }

      if (props.processingActivityId !== undefined) {
        entityType = EntityTypes.ProcessingActivity;
        entityId = props.processingActivityId;
      }

      if (props.internalAuditId !== undefined) {
        entityType = EntityTypes.InternalAudit;
        entityId = props.internalAuditId;
      }

      if (props.auditCriterionId !== undefined) {
        entityType = EntityTypes.AuditCriterion;
        entityId = props.auditCriterionId;
      }

      //save entity for updates
      const entity = new Entity(entityId, entityType);
      setEntity(entity);

      const commentTrailId = props.commentTrailId ? props.commentTrailId : 0;
      const auditTrailId = props.auditTrailId ? props.auditTrailId : 0;
      let activityTrail: ActivityTrailModel; //alias for ActivityTrail due to conflict with this class

      if (props.tenantId) {
        activityTrail = await apiGetActivityTrailForTenant(
          props.tenantId,
          auditTrailId,
          commentTrailId,
          entityType,
          entityId,
          accessToken,
        );
      } else {
        activityTrail = await apiGetActivityTrail(auditTrailId, commentTrailId, entityType, entityId, accessToken);
      }

      // The change log holds every change; the activity feed only shows the ones that read as an
      // event. getActivityText returns '' for the rest, so it is the whitelist: entries without a
      // translation are left out instead of rendering as empty rows.
      const auditTrailEntries = activityTrail.auditTrailEntries.filter(
        (entry) => entry.getActivityText(t as unknown as TFunction<string[]>, appContext) !== '',
      );

      //sort on creation date descending
      const activities: (CommentTrailEntry | Activity | AuditTrailEntry)[] = [
        ...activityTrail.commentTrail.entries,
        ...auditTrailEntries,
        ...activityTrail.activities,
      ];

      activities.sort((a, b) => {
        const d1: Date = a.created;
        const d2: Date = b.created;

        if (d1 === d2) return 0;

        return d1 > d2 ? -1 : 1;
      });

      const activitiesToDisplay: (CommentTrailEntry | Activity | AuditTrailEntry)[] = onlyShowComments
        ? [...activityTrail.commentTrail.entries]
        : activities;

      hasLoaded.current = true;
      setActivities(activities);
      setActivitiesDisplay(activitiesToDisplay);
    } catch (err) {
      if (props.onError) {
        props.onError(err);
      } else {
        appContext.setError(err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onAddComment = async (newCommentValue: string, users: User[]) => {
    try {
      if (!props.commentTrailId) {
        return;
      }
      setIsActionPending(true);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const comment = new CommentTrailEntry();
      comment.commentTrailId = props.commentTrailId;
      comment.created = new Date();
      comment.comment = newCommentValue;

      let newComment: CommentTrailEntry;
      if (props.tenantId) {
        comment.createdById = newGuidNil();
        comment.createdBy = globalAdminTenantCommentName;
        newComment = await apiAddCommentForSpecificTenant(props.tenantId, comment, accessToken);
      } else {
        newComment = await apiAddComment(comment, accessToken);
      }

      const newActivities = [newComment, ...activities];
      const newActivitiesDisplay = [newComment, ...activitiesDisplay];
      setActivities(newActivities);
      setActivitiesDisplay(newActivitiesDisplay);

      //callback with the new comment
      if (props.onAddComment) {
        props.onAddComment(newComment);
      }

      //send adaptive card to the mentioned users in Teams
      // if (props.taskId && users && users.length > 0) {
      //   const tasks = await apiGetSingleTask(props.taskId, false, accessToken, appContext.globalDataCache);
      //   const graphInterface = await appContext.getGraphInterface(graphTeamsCollaborationRequest.scopes);
      //   if (tasks && tasks.tasks.length > 0) {
      //     const content = adaptiveCardChatTaskComment(tasks.tasks[0], newCommentValue, t);

      //     const result = await graphSendChatOneToOne(graphInterface.client, users, appContext.user, content);
      //     if (!result) {
      //       appContext.showNotification('Could not send message to mentioned users', true);
      //     }
      //   }
      // }
    } catch (err) {
      if (props.onError) {
        props.onError(err);
      } else {
        appContext.setError(err);
      }
    } finally {
      setIsActionPending(false);
    }
  };

  const onRemoveComment = async (commentTrailId: number, commentTrailEntryId: number) => {
    try {
      if (!commentTrailEntryId || !commentTrailId || !entity) {
        return;
      }
      setIsActionPending(true);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      await apiRemoveComment(commentTrailId, commentTrailEntryId, entity, accessToken);

      const newActivitiesDisplay: (CommentTrailEntry | Activity | AuditTrailEntry)[] = activitiesDisplay.filter(
        (activity: CommentTrailEntry | Activity | AuditTrailEntry) => {
          if (activity instanceof CommentTrailEntry) return activity.commentTrailEntryId !== commentTrailEntryId;

          return true;
        },
      );

      setActivities(
        activities.filter(
          (activity) =>
            !(activity instanceof CommentTrailEntry && activity.commentTrailEntryId === commentTrailEntryId),
        ),
      );
      setActivitiesDisplay(newActivitiesDisplay);
    } catch (err) {
      if (props.onError) {
        props.onError(err);
      } else {
        appContext.setError(err);
      }
    } finally {
      setIsActionPending(false);
    }
  };

  const onUpdateComment = async (existingComment: CommentTrailEntry, updatedComment: string) => {
    try {
      if (!entity) return;

      setIsActionPending(true);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);

      existingComment.comment = updatedComment;
      existingComment.modified = new Date();
      existingComment.modifiedById = appContext.user.id;

      const newComment = await apiUpdateComment(existingComment, entity, accessToken);

      const existingCommentIndex = activities.findIndex((activity: CommentTrailEntry | Activity | AuditTrailEntry) => {
        if (activity instanceof CommentTrailEntry)
          return activity.commentTrailEntryId === existingComment.commentTrailEntryId;

        return false;
      });

      const newActivities = [...activities];
      newActivities[existingCommentIndex] = newComment;

      const existingCommentDisplayIndex = activitiesDisplay.findIndex(
        (activity: CommentTrailEntry | Activity | AuditTrailEntry) => {
          if (activity instanceof CommentTrailEntry)
            return activity.commentTrailEntryId === existingComment.commentTrailEntryId;

          return false;
        },
      );

      const newActivitiesDisplay = [...activitiesDisplay];
      newActivitiesDisplay[existingCommentDisplayIndex] = newComment;
      setActivities(newActivities);
      setActivitiesDisplay(newActivitiesDisplay);
    } catch (err) {
      if (props.onError) {
        props.onError(err);
      } else {
        appContext.setError(err);
      }
    } finally {
      setIsActionPending(false);
    }
  };

  const onQueueComment = (newCommentValue: string) => {
    if (newCommentValue) {
      props.onQueueComment?.(newCommentValue);
    }
  };

  const getKey = (activity: CommentTrailEntry | Activity | AuditTrailEntry) => {
    if (activity instanceof CommentTrailEntry) {
      return activity.commentTrailEntryId;
    } else if (activity instanceof AuditTrailEntry) {
      return activity.auditTrailEntryId;
    } else {
      return activity.activityId;
    }
  };

  const onFilter = (ev: React.ChangeEvent<HTMLInputElement> | undefined, text: string | undefined): void => {
    if (text === undefined || text.length < globalMinFilterChars) {
      const newActivities = onlyShowComments
        ? [...activities.filter((a) => a instanceof CommentTrailEntry)]
        : activities;
      setActivitiesDisplay(newActivities);
    } else {
      let newActivityEntries = activities.filter((a) => a instanceof Activity) as Activity[];
      let newCommentEntries = activities.filter((a) => a instanceof CommentTrailEntry) as CommentTrailEntry[];
      let newAuditTrailEntries = activities.filter((a) => a instanceof AuditTrailEntry) as AuditTrailEntry[];

      const textLower = text.toLowerCase();

      newActivityEntries = newActivityEntries.filter(
        (a) =>
          a.getPrimaryText(appContext.globalDataCache).toLowerCase().indexOf(textLower) >= 0 ||
          a.getSecondaryText(appContext.globalDataCache).toLowerCase().indexOf(textLower) >= 0 ||
          a.getUserName(appContext.globalDataCache)?.toLowerCase().indexOf(textLower) >= 0,
      );

      newCommentEntries = newCommentEntries.filter(
        (a) =>
          a.comment.toLowerCase().indexOf(textLower) >= 0 ||
          appContext.globalDataCache.users.get(a.createdById).name.toLowerCase().indexOf(textLower) >= 0 ||
          appContext.globalDataCache.users.get(a.modifiedById).name.toLowerCase().indexOf(textLower) >= 0,
      );

      newAuditTrailEntries = newAuditTrailEntries.filter(
        (a) =>
          a
            .getActivityText(t as unknown as TFunction<string[]>, appContext)
            .toLowerCase()
            .indexOf(textLower) >= 0 || a.userName.indexOf(textLower) >= 0,
      );

      //sort on creation date descending
      const newActivities: (CommentTrailEntry | Activity | AuditTrailEntry)[] = onlyShowComments
        ? [...newCommentEntries]
        : [...newActivityEntries, ...newCommentEntries, ...newAuditTrailEntries];

      newActivities.sort((a, b) => {
        const d1: Date = a.created;
        const d2: Date = b.created;

        if (d1 === d2) return 0;

        return d1 > d2 ? -1 : 1;
      });

      setActivitiesDisplay(newActivities);
    }
  };

  //
  // Main render
  //
  if (isQueueMode) {
    const pendingComments = props.pendingComments ?? [];

    return (
      <IPStack verticalFill tokens={globalStackTokensGapMedium}>
        <ScrollableStackItem minHeight={100}>
          {pendingComments.length === 0 && (
            <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
              <IPText variant="medium">{t('commentTrail:NoActivity')}</IPText>
            </IPStack>
          )}
          {/* newest first, like the saved messages; index points into the queue (oldest first) */}
          {pendingComments
            .map((comment, index) => ({ comment, index }))
            .reverse()
            .map((item) => (
              <PendingComment
                key={item.index}
                comment={item.comment}
                onRemove={() => props.onRemovePendingComment?.(item.index)}
              />
            ))}
        </ScrollableStackItem>
        <IPStack.Item>
          <AddComment onSend={onQueueComment} isActionPending={false} />
        </IPStack.Item>
      </IPStack>
    );
  }

  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  return (
    <IPStack verticalFill tokens={globalStackTokensGapMedium}>
      <IPStack.Item>
        <IPSearchBox
          value={searchText}
          underlined
          placeholder={t('translation:General.Filter.Placeholder')}
          id="search"
          onChange={(ev, newValue) => {
            setSearchText(newValue ?? '');
            if (searchTimer) {
              clearTimeout(searchTimer);
            }
            setSearchTimer(setTimeout(() => onFilter(ev, newValue), globalFilterDelay));
          }}
          styles={{ root: { width: 300 } }}
        />
      </IPStack.Item>
      <IPStack.Item>
        <IPCheckbox
          label={t('commentTrail:OnlyMessagesCheckbox.Label')}
          checked={onlyShowComments}
          onChange={(ev, checked) => {
            setLocalStorageData(
              appContext,
              LocalStorageKeys.OnlyShowCommentsInActivityTab,
              checked?.toString() ?? 'false',
            );
            setOnlyShowComments(checked ?? false);
          }}
        />
      </IPStack.Item>
      <ScrollableStackItem minHeight={100}>
        {activities.length === 0 && (
          <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
            <IPText variant="medium">{t('commentTrail:NoActivity')}</IPText>
          </IPStack>
        )}
        {activities.length > 0 && (
          <IPList
            items={activitiesDisplay}
            onRenderCell={(activity) => {
              if (!activity) return;

              return (
                <ActivityTrailItem
                  key={getKey(activity)}
                  onModifyComment={onUpdateComment}
                  onRemoveComment={onRemoveComment}
                  isActionPending={isActionPending}
                  ActivityItem={activity}
                  readOnly={!canUpdate || props.tenantId !== undefined}
                />
              );
            }}
          />
        )}
      </ScrollableStackItem>
      {/* the frozen record takes no new comments. Gated on the prop and not on canUpdate: canUpdate
          also folds in the Update permission, and hiding the composer on that would silence readers
          on every entity's activity tab, which is not what this prop is for */}
      {props.readOnly !== true && (
        <IPStack.Item>
          <AddComment onSend={onAddComment} isActionPending={isActionPending} />
        </IPStack.Item>
      )}
    </IPStack>
  );
};

export default ActivityTrail;
